// SharedView.js - View shared content via token
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getShareToken, isAccessibleThroughShare } from '../services/sharing';
import { signInAnonymously } from 'firebase/auth';
import { auth } from '../firebase';
import RunsList from './RunsList';
import RacersList from './RacersList';
import GroupsList from './GroupsList';

const SharedView = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tokenData, setTokenData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadToken = async () => {
      if (!token) {
        setError('Invalid share link');
        setLoading(false);
        return;
      }

      try {
        // Sign in anonymously if not authenticated
        let currentUser = user;
        if (!currentUser) {
          try {
            const userCredential = await signInAnonymously(auth);
            currentUser = userCredential.user;
            // Wait a bit for auth state to propagate
            await new Promise(resolve => setTimeout(resolve, 500));
          } catch (err) {
            console.error('Anonymous sign-in error:', err);
            setError('Failed to authenticate. Please try again.');
            setLoading(false);
            return;
          }
        }

        const data = await getShareToken(token);
        if (!data) {
          setError('Share link not found or expired');
          setLoading(false);
          return;
        }

        // Check if expired
        if (data.expiresAt && data.expiresAt.toDate() < new Date()) {
          setError('This share link has expired');
          setLoading(false);
          return;
        }

        // Check if revoked
        if (data.revoked) {
          setError('This share link has been revoked');
          setLoading(false);
          return;
        }

        // Check permission for editor
        if (data.permission === 'editor' && (!currentUser || currentUser.isAnonymous)) {
          setError('Editor access requires a signed-in account. Please sign in to continue.');
          setLoading(false);
          return;
        }

        setTokenData(data);
      } catch (err) {
        console.error('Error loading share token:', err);
        setError('Failed to load share link. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadToken();
  }, [token, user]);

  // Update URL to include token parameter
  useEffect(() => {
    if (tokenData && token) {
      const currentUrl = new URL(window.location.href);
      if (!currentUrl.searchParams.has('token')) {
        currentUrl.searchParams.set('token', token);
        window.history.replaceState({}, '', currentUrl);
      }
    }
  }, [tokenData, token]);

  if (loading) {
    return (
      <div style={{ 
        width: '100%', 
        padding: '40px 20px', 
        textAlign: 'center' 
      }}>
        <p>Loading shared content...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        width: '100%', 
        padding: '40px 20px', 
        textAlign: 'center' 
      }}>
        <p style={{ color: '#d32f2f', marginBottom: '20px' }}>{error}</p>
        {error.includes('sign in') && (
          <button
            onClick={() => navigate('/login')}
            style={{
              padding: '10px 20px',
              backgroundColor: '#13A1A8',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 'medium'
            }}
          >
            Sign In
          </button>
        )}
      </div>
    );
  }

  if (!tokenData) {
    return null;
  }

  // Route to appropriate view based on resource type
  // Pass token via URL params so list components can filter
  const resourceType = tokenData.resourceType;
  
  if (resourceType === 'run' || resourceType === 'all_racers') {
    // For runs or all racers, show runs list
    return <RunsList />;
  } else if (resourceType === 'racer') {
    // For individual racer, show racers list filtered to that racer
    return <RacersList />;
  } else if (resourceType === 'group') {
    // For group, show groups list
    return <GroupsList />;
  }

  return (
    <div style={{ 
      width: '100%', 
      padding: '40px 20px', 
      textAlign: 'center' 
    }}>
      <p>Unknown share type: {resourceType}</p>
    </div>
  );
};

export default SharedView;
